// Three-way merge of a note's frontmatter block, for when both sides of a
// sync changed it (see "Frontmatter" in PLAN.md). Top-level keys are entries
// whose lines are copied, never re-serialised, so every client produces the
// same bytes. Mirrored by the Swift and Kotlin ports, which run the same
// frontmatterMerge.fixtures.json. Change all three or none.

export interface FrontmatterBlock {
  /** The text between the fences, or null when the text has no block. */
  inner: string | null
  body: string
}

/**
 * The block starts with `---\n` and ends at the first `\n---\n` searched from
 * index 4: `splitFrontmatterDocument`'s rule. So `---\n---\n` is not a block,
 * and when there is one it spans `inner.length + 9` code units.
 */
export function splitFrontmatterBlock(text: string): FrontmatterBlock {
  if (!text.startsWith("---\n")) return { inner: null, body: text }
  const end = text.indexOf("\n---\n", 4)
  if (end === -1) return { inner: null, body: text }
  return { inner: text.slice(4, end), body: text.slice(end + 5) }
}

export function assembleFrontmatter(inner: string | null, body: string): string {
  return inner === null ? body : `---\n${inner}\n---\n${body}`
}

const KEY = /^([A-Za-z0-9_-]+):/
const FOLDED_HEADER = /^folded:\s*$/

type Entry = [key: string, lines: string[]]

// Entries in order; lines before the first key go under ''.
function entries(inner: string | null): Entry[] {
  const out: Entry[] = []
  if (inner === null) return out
  for (const line of inner.split("\n")) {
    const m = KEY.exec(line)
    if (m) out.push([m[1], [line]])
    else if (out.length) out[out.length - 1][1].push(line)
    else out.push(["", [line]])
  }
  return out
}

// Three-way on strings (undefined = absent): the side that changed wins; both changed: remote.
function pick(b: string | undefined, l: string | undefined, r: string | undefined): string | undefined {
  if (l === r) return l
  if (l === b) return r
  if (r === b) return l
  return r
}

// `folded:` items when the entry is a plain `folded:` header followed only by
// "  - " items (absent = no items); null for any other shape.
function foldItems(lines: string[] | undefined): string[] | null {
  if (!lines) return []
  if (!FOLDED_HEADER.test(lines[0])) return null
  const items = lines.slice(1)
  return items.every((line) => line.startsWith("  - ")) ? items : null
}

/**
 * Per key, the side that changed wins; remote wins when both changed
 * (deletion included). `folded:` is a set: an item stays when both sides have
 * it or one side added it. Order: remote's keys, then local-only ones. An
 * empty result is null (no block). `reminders:` is an ordinary key.
 */
export function mergeFrontmatter(base: string | null, local: string | null, remote: string | null): string | null {
  const b = new Map(entries(base))
  const l = new Map(entries(local))
  const r = new Map(entries(remote))
  const keys = [...r.keys(), ...[...l.keys()].filter((k) => !r.has(k))]
  const out: string[] = []
  for (const k of keys) {
    const bv = b.get(k)
    const lv = l.get(k)
    const rv = r.get(k)
    const lj = lv?.join("\n")
    const rj = rv?.join("\n")
    if (lj === rj) {
      if (lj !== undefined) out.push(lj)
      continue
    }
    if (k === "folded") {
      const fb = foldItems(bv)
      const fl = foldItems(lv)
      const fr = foldItems(rv)
      if (fb && fl && fr) {
        const kept = [
          ...fr.filter((i) => fl.includes(i) || !fb.includes(i)),
          ...fl.filter((i) => !fr.includes(i) && !fb.includes(i)),
        ]
        // lj !== rj, so one side has the entry; its header line is copied.
        if (kept.length) out.push((rv ?? lv ?? ["folded:"])[0], ...kept)
        continue
      }
    }
    const v = pick(bv?.join("\n"), lj, rj)
    if (v !== undefined) out.push(v)
  }
  return out.length ? out.join("\n") : null
}
